//
//  Machines.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 22, 2026
//  Author: Bob Keathley
//
//  Machines page. MachineUsers shows the people registered on one machine and
//  their device sessions there (spec 6m).
//
import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { DEVICE_REVOKE_MIN_ROLE, enrolledText, type EnrollCode, type Machine, type MachineUser, type MintedCode, type Page } from '@shared/api/types'
import { atLeast, type Session } from '@shared/auth'
import { ago } from './Fleet'
import { DevicesPanel } from './Users'

const when = (s: number | null) => (s ? new Date(s * 1000).toLocaleString() : '—')

/** §6m: the people registered on one machine (doc 28 triple) and, per person, their device sessions on this machine. */
export function MachineUsers({ machine, session }: { machine: Machine; session: Session }) {
  const [items, setItems] = useState<MachineUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    const r = await api<{ items: MachineUser[] }>(`/machines/${machine.id}/users?include_removed=true`, {}, session.token)
    if (isOk(r)) { setItems(r.data.items); setError(null) } else { setItems([]); setError(r.error.message) }
  }, [machine.id, session.token])
  useEffect(() => { load() }, [load])
  async function remove(u: MachineUser) {
    if (!confirm(`Remove ${u.user.email} (${u.os_user}) from ${machine.hostname}? Their device sessions on this machine are revoked.`)) return
    const r = await api<MachineUser>(`/machines/${machine.id}/users/${u.id}`, { method: 'DELETE' }, session.token)
    if (!isOk(r)) setError(r.error.message); else load()
  }
  const canAdmin = atLeast(session.user.role, DEVICE_REVOKE_MIN_ROLE)
  return (
    <div className="card" aria-label={`Users on ${machine.hostname}`}>
      {error && <p className="err" role="alert">{error}</p>}
      <table>
        <thead><tr><th>OS user</th><th>Person</th><th>Added</th><th>Removed</th><th></th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={5} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && <tr><td colSpan={5} className="hint">No users registered on this machine.</td></tr>}
          {items?.map((u) => (
            <tr key={u.id}>
              <td className="mono">{u.os_user}</td>
              <td>{u.user.display_name} <span className="hint mono">{u.user.email}</span></td>
              <td className="hint">{when(u.created_at)}</td>
              <td>{u.removed_at ? <span className="pill off">removed {when(u.removed_at)}</span> : <span className="hint">—</span>}</td>
              <td style={{ textAlign: 'right' }}>{!u.removed_at && canAdmin && <button className="btn danger sm" onClick={() => remove(u)}>Remove</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items?.filter((u) => !u.removed_at).map((u) => (
        <div key={u.id}>
          <h3>Device sessions — {u.user.email} ({u.os_user})</h3>
          <DevicesPanel token={session.token} userId={u.user_id} self={u.user_id === session.user.id} canRevoke={u.user_id === session.user.id || canAdmin} onlyMachine={machine.id} />
        </div>
      ))}
    </div>
  )
}

export default function Machines({ session }: { session: Session }) {
  const [machines, setMachines] = useState<Machine[] | null>(null)
  const [codes, setCodes] = useState<EnrollCode[]>([])
  const [minted, setMinted] = useState<MintedCode | null>(null)
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [m, c] = await Promise.all([api<Page<Machine>>('/machines', {}, session.token), api<Page<EnrollCode>>('/machines/enroll-codes', {}, session.token)])
    if (isOk(m)) setMachines(m.data.items); else { setMachines([]); setError(m.error.message) }
    if (isOk(c)) setCodes(c.data.items)
  }, [session.token])
  useEffect(() => { setMinted(null); load() }, [load])

  async function mint(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null)
    const r = await api<MintedCode>('/machines/enroll-codes', { method: 'POST', body: JSON.stringify({ label }) }, session.token)
    setBusy(false)
    if (!isOk(r)) { setError(r.error.message); return }
    setMinted(r.data); setLabel(''); load()
  }
  async function patch(m: Machine, body: { revoked?: boolean; label?: string }) {
    const r = await api<Machine>(`/machines/${m.id}`, { method: 'PATCH', body: JSON.stringify(body) }, session.token)
    if (!isOk(r)) setError(r.error.message); else load()
  }
  const copy = (t: string) => navigator.clipboard?.writeText(t).catch(() => {})

  return (
    <>
      <h2>Enrol a machine</h2>
      <p className="hint">A code is shown once, expires in 60 minutes and can be redeemed once. The machine runs the command; the server keeps only the code's hash.</p>
      <form className="row" onSubmit={mint} aria-label="Mint enrollment code">
        <div><label htmlFor="c-label">Label (which machine is this for?)</label><input id="c-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="bob-studio" /></div>
        <div className="fixed"><button className="btn" type="submit" disabled={busy}>{busy ? 'Minting…' : 'Mint code'}</button></div>
      </form>
      {minted && (
        <div className="codebox" role="status">
          <div className="l">Enrollment code — copy it now, it will not be shown again</div>
          <div className="code">{minted.code} <button className="btn ghost sm" onClick={() => copy(minted.code)}>copy</button></div>
          <div className="mono hint">{minted.command} <button className="btn ghost sm" onClick={() => copy(minted.command)}>copy</button></div>
          <div className="hint">expires {when(minted.expires_at)}</div>
        </div>
      )}
      {error && <p className="err" role="alert">{error}</p>}

      <h2>Machines</h2>
      <table>
        <thead><tr><th>Hostname</th><th>Label</th><th>Client</th><th>Enrolled</th><th>Added by</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {machines === null && <tr><td colSpan={8} className="hint">Loading…</td></tr>}
          {machines?.length === 0 && <tr><td colSpan={8} className="hint">No machines enrolled.</td></tr>}
          {machines?.map((m) => (
            <Fragment key={m.id}>
            <tr>
              <td className="mono"><button className="btn ghost sm" aria-expanded={open === m.id} onClick={() => setOpen(open === m.id ? null : m.id)}>Users</button> {m.hostname}</td>
              <td><input defaultValue={m.label} placeholder="label" style={{ width: 160 }} onBlur={(e) => { if (e.target.value !== m.label) patch(m, { label: e.target.value }) }} /></td>
              <td className="mono hint">{m.client_build || '—'}</td>
              <td className="hint">{when(m.created_at)}</td>
              <td className="hint" title={m.enrolled_by?.email}>{enrolledText(m)}</td>
              <td className="hint">{ago(m.last_seen_at)}</td>
              <td>{m.revoked_at ? <span className="pill off">revoked</span> : <span className="pill ok">active</span>}</td>
              <td style={{ textAlign: 'right' }}>
                {m.revoked_at
                  ? <button className="btn ghost sm" onClick={() => patch(m, { revoked: false })}>Restore</button>
                  : <button className="btn danger sm" onClick={() => { if (confirm(`Revoke ${m.hostname}? It will be unable to sync until restored.`)) patch(m, { revoked: true }) }}>Revoke</button>}
              </td>
            </tr>
            {open === m.id && <tr><td colSpan={8}><MachineUsers machine={m} session={session} /></td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table>

      <h2>Enrollment codes</h2>
      <table>
        <thead><tr><th>Label</th><th>Minted</th><th>Expires</th><th>State</th></tr></thead>
        <tbody>
          {codes.length === 0 && <tr><td colSpan={4} className="hint">No codes minted.</td></tr>}
          {codes.map((c) => {
            const state = c.redeemed_at ? 'redeemed' : c.expires_at * 1000 < Date.now() ? 'expired' : 'pending'
            return (
              <tr key={c.id}>
                <td>{c.label || <span className="hint">—</span>}</td>
                <td className="hint">{when(c.created_at)}</td>
                <td className="hint">{when(c.expires_at)}</td>
                <td><span className={`pill ${state === 'pending' ? 'watchp' : state === 'redeemed' ? 'ok' : 'off'}`}>{state}</span>{c.redeemed_machine_id && <span className="hint mono"> → {machines?.find((m) => m.id === c.redeemed_machine_id)?.hostname ?? c.redeemed_machine_id.slice(0, 8)}</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}
